// @flow @jsx h
import { h, Component } from 'preact'
import { bem } from '@nrk/dh-utils'
import { ViewportIntersections, HtmlContent } from '@nrk/dh-feature-components'
import { connect } from '../../lib/provider'
import { Intro } from './Intro'
import { TekstBlokk } from './blokker/TekstBlokk'
import { BildeBlokk } from './blokker/BildeBlokk'
import { DobbelBildeBlokk } from './blokker/DobbelBildeBlokk'
import { VitenskapBlokk } from './blokker/VitenskapBlokk'
import { LevendeStillbildeBlokk } from './blokker/LevendeStillbildeBlokk'
import { StedintroVideoBlokk } from './blokker/StedintroVideoBlokk'
import { EpisidevelgerBlokk } from './blokker/EpisodevelgerBlokk'
import { StedintroBildeBlokk } from './blokker/StedintroBildeBlokk'
import { ImageGallery } from './blokker/ImageGallery'
import { VisuellHistorie } from './blokker/VisuellHistorie'
import { VideoHistorie } from './blokker/VideoHistorie'
import { VisuellHistorieScroll } from './blokker/VisuellHistorieScroll'
import { Footer } from './Footer'
import { Byline } from './Byline'

/* types */
import type { Doc } from './domain'
import MaxViewportRect from './MaxViewportRect'

/* style */
import styles from './App.css'

const LOADABLE_CONTENT = ['visuellHistorieBlokk', 'visuellHistorieScrollBlokk', 'bildeBlokk', 'episodevelgerBlokk', 'stedintroBildeBlokk', 'imageGallery']
const ACTIVATABLE_CONTENT = ['videoBlokk', 'levendeStillbildeBlokk', 'visuellHistorieBlokk', 'videoHistorie']
const FULLY_VISIBLE_CONTENT = ['stedintroVideoBlokk', 'stedintroBildeBlokk']

type Props = {
  sendGaEvent: (Object) => void,
  doc: Doc
}

type State = {
  viewport: { height: number, width: number },
  visibleContent: any,
  fullyVisibleContent: any,
  loadContent: any,
  introVisible: boolean,
  backgroundVisible: boolean
}

class _App extends Component<Props, State> {
  elm: HTMLElement
  trackedKeys = {}

  constructor () {
    super()
    this.state = {
      viewport: { height: -1, width: -1 },
      visibleContent: [],
      fullyVisibleContent: [],
      loadContent: [],
      backgroundVisible: false,
      introVisible: true
    }
  }

  getContenttype (idx: number) {
    const contentList = this.props.doc.tekst
    if (idx < 0 || idx >= contentList.length) {
      return null
    } else {
      return contentList[idx].type
    }
  }

  ensureLoaded (idx: number) {
    const type = this.getContenttype(idx)
    if (LOADABLE_CONTENT.indexOf(type) !== -1) {
      const loadContent = this.state.loadContent
      if (!loadContent[idx]) {
        loadContent[idx] = true
        this.setState({ loadContent })
      }
    }
  }

  handleViewportChange = (viewport: any) => {
    if (
      this.state.viewport.height !== viewport.height ||
      this.state.viewport.width !== viewport.width
    ) {
      this.elm.style.setProperty('--viewport-height', `${viewport.height}px`)
      this.elm.style.setProperty('--viewport-height--half', `${viewport.height / 2}px`)
      this.setState({ viewport })
    }
  }

  handleContentEnter = (element: any, idx: number) => {
    this.ensureLoaded(idx - 1)
    this.ensureLoaded(idx)
    this.ensureLoaded(idx + 1)

    const contentList = this.props.doc.tekst
    const content = contentList[idx]

    if (ACTIVATABLE_CONTENT.indexOf(content.type) !== -1) {
      const activeContent = this.state.visibleContent
      activeContent[idx] = true
      this.setState({ visibleContent: activeContent })
    }

    this.trackOnlyOnce(idx, 'SHOW_CONTENT', `${idx}/${content.type}`)
  }

  handleContentLeave = (element: any, idx: number) => {
    const contentList = this.props.doc.tekst
    const content = contentList[idx]

    if (ACTIVATABLE_CONTENT.indexOf(content.type) !== -1) {
      const activeContent = this.state.visibleContent
      activeContent[idx] = false
      this.setState({ visibleContent: activeContent })
    }
  }

  handleContentChange = (intersections: any[]) => {
    const { fullyVisibleContent } = this.state
    const contentList = this.props.doc.tekst
    let change = false

    intersections.forEach((block, idx) => {
      const content = contentList[idx]
      if (FULLY_VISIBLE_CONTENT.indexOf(content.type) === -1) {
        return
      }

      const isVisible = !!fullyVisibleContent[idx]
      if (isVisible && block.ratio < 0.05) {
        fullyVisibleContent[idx] = false
        change = true
      } else if (!isVisible && block.isIntersecting && block.ratio > 0.95) {
        fullyVisibleContent[idx] = true
        change = true
      }
    })

    if (change) {
      this.setState({ fullyVisibleContent })
    }
  }

  handleIntroEnter = () => {
    this.setState({ introVisible: true })
  }

  handleIntroLeave = () => {
    this.setState({ introVisible: false })
  }

  handleBackgroundEnter = () => {
    this.setState({ backgroundVisible: true })
  }

  handleBackgroundLeave = () => {
    this.setState({ backgroundVisible: false })
  }

  trackOnlyOnce (key, action: string, label: string) {
    if (!this.trackedKeys[key]) {
      this.trackedKeys[key] = true
      this.props.sendGaEvent && this.props.sendGaEvent({ action, label })
    }
  }

  render () {
    const { doc } = this.props
    const { viewport, introVisible, backgroundVisible } = this.state

    return (
      <article
        class={bem(styles.app, doc.theme || 'dark', { optimizeIntro: introVisible })}
        ref={elm => (this.elm = elm)}>
        <MaxViewportRect
          height='100'
          onViewportChange={this.handleViewportChange}
        />
        <a
          className={bem(styles.nrklogo, { dark: !this.state.introVisible })}
          href='http://www.nrk.no/'
          title='Forsiden av NRK.no'>
          <svg width='48.75px' height='18px' aria-hidden='true'>
            <use xlinkHref='#nrk-logo-nrk' />
          </svg>
        </a>
        <ViewportIntersections
          className={styles.intro}
          threshold={[0, 0.5, 1]}
          onEnter={this.handleIntroEnter}
          onLeave={this.handleIntroLeave}>
          <Intro
            intro={doc.intro}
            stikktittel={doc.stikktittel}
            fornavn={doc.fornavn}
            etternavn={doc.etternavn}
            viewport={viewport}
            introVideo={doc.introVideo}
            profilbilde={doc.profilbilde}
            bylinebilde={doc.bylinebilde}
            byline={doc.byline} />
        </ViewportIntersections>

        <ViewportIntersections
          threshold={[0]}
          onEnter={this.handleBackgroundEnter}
          onLeave={this.handleBackgroundLeave}>
          <div className={bem(styles.background, { backgroundVisible })}>
            <Byline
              byline={doc.byline}
              bylinebilde={doc.bylinebilde}
              load />

            <ViewportIntersections
              onChange={this.handleContentChange}
              onEnter={this.handleContentEnter}
              onLeave={this.handleContentLeave}
              threshold={[0, 1]}
              className={styles.content}>
              {
                (doc.tekst || []).map((item, idx) => {
                  switch (item.type) {
                    case 'text':
                      return <HtmlContent
                        key={idx}
                        type='p'
                        content={item.value} />
                    case 'tekstBlokk':
                      return <TekstBlokk key={idx} blockId={idx} tekst={item.value.tekst} />
                    case 'vitenskapBlokk':
                      return <VitenskapBlokk key={idx} tekst={item.value.tekst} />
                    case 'bildeBlokk':
                      return <BildeBlokk
                        key={idx}
                        id={item.value.polopolyId}
                        alt={item.value.alt}
                        caption={item.value.caption}
                        load={this.state.loadContent[idx]} />
                    case 'dobbelBildeBlokk':
                      return <DobbelBildeBlokk key={idx} images={item.value.images} />
                    case 'levendeStillbildeBlokk':
                      return <LevendeStillbildeBlokk
                        key={idx}
                        video={item.value.video || item.value.horizontal}
                        caption={item.value.caption}
                        playing={this.state.visibleContent[idx]}
                      />
                    case 'imageGallery':
                      return <ImageGallery images={item.value.images} load={this.state.loadContent[idx]} />
                    case 'stedintroVideoBlokk':
                      return <StedintroVideoBlokk
                        key={idx}
                        horizontal={item.value.horizontal || item.value.video}
                        vertical={item.value.vertical}
                        sted={item.value.sted}
                        info={item.value.info}
                        alt={item.value.alt}
                        viewport={viewport}
                        playing={this.state.fullyVisibleContent[idx]} />
                    case 'stedintroBildeBlokk':
                      return <StedintroBildeBlokk
                        key={idx}
                        polopolyId={item.value.polopolyId}
                        sted={item.value.sted}
                        info={item.value.info}
                        alt={item.value.alt}
                        load={this.state.loadContent[idx]}
                        playing={this.state.fullyVisibleContent[idx]}
                      />
                    case 'visuellHistorieBlokk':
                    case 'visuellHistorie':
                      return <VisuellHistorie
                        key={idx}
                        blockId={idx}
                        cards={item.value.cards}
                        align={item.value.align}
                        active={this.state.visibleContent[idx]}
                        load={this.state.loadContent[idx]}
                      />
                    case 'visuellHistorieScrollBlokk':
                    case 'visuellHistorieScroll':
                      return <VisuellHistorieScroll
                        key={idx}
                        blockId={idx}
                        cards={item.value.cards}
                        align={item.value.align}
                        active={this.state.visibleContent[idx]}
                        load={this.state.loadContent[idx]}
                      />
                    case 'videoHistorie':
                      return <VideoHistorie
                        key={idx}
                        blockId={idx}
                        video={item.value.video}
                        cards={item.value.cards}
                        align={item.value.align}
                        active={this.state.visibleContent[idx]}
                        load={this.state.loadContent[idx]}
                      />
                    case 'episodevelgerBlokk':
                      return <EpisidevelgerBlokk
                        episoder={item.value.episoder}
                        load={this.state.loadContent[idx]} />
                    default:
                      return <pre key={idx} style={{ overflow: 'scroll' }}>
                        {JSON.stringify(item, null, 2)}
                      </pre>
                  }
                })
              }
            </ViewportIntersections>
            {doc.footer && <Footer footer={doc.footer} />}
          </div>
        </ViewportIntersections>
      </article>
    )
  }
}

/*
const mapContextToProps = context => ({
  ...context,
  sendCustomGaEvent: () => context.sendEvent({ action: 'my custom event action' })
})
*/

const connector = connect()
// const connector = connect(mapContextToProps)
export const App = connector(_App)
